import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import LoginPage from './pages/auth/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Previsions from './pages/Previsions';
import Approvisionnement from './pages/Approvisionnement';
import CyclesDeVie from './pages/CyclesDeVie';
import Alertes from './pages/Alertes';
import Promotions from './pages/Promotions';
import MesDonnees from './pages/MesDonnees';
import DatabaseManage from './pages/DatabaseManage';
import { DatabaseProvider, useDatabaseContext } from './contexts/DatabaseContext';
import Commandes from './pages/Commandes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Configuration avancée du QueryClient pour le débogage
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Composant pour les routes protégées
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isAuthChecking } = useAuth();
  const location = useLocation();
  const { activeDatabase, isLoading: isDbLoading } = useDatabaseContext();
  
  // Show loading while checking authentication
  if (isAuthChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Vérification de l'authentification...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to Mes Données when no active database is selected,
  // except for routes that are valid without an active database
  const isDbOptionalRoute =
    location.pathname === '/mes-donnees' ||
    (location.pathname.startsWith('/databases/') && location.pathname.endsWith('/manage'));

  if (!isDbOptionalRoute) {
    if (isDbLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-gray-600">Chargement de la base de données...</div>
        </div>
      );
    }
    if (!activeDatabase) {
      return <Navigate to="/mes-donnees" replace />;
    }
  }

  return <MainLayout>{children}</MainLayout>;
};

// Login route component to handle authentication check
const LoginRoute = () => {
  const { isAuthenticated, isAuthChecking } = useAuth();
  
  // Show loading while checking authentication
  if (isAuthChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Vérification de l'authentification...</div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/mes-donnees" replace />;
  }
  
  return <LoginPage />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <DatabaseProvider>
          <BrowserRouter>
            <Routes>
              {/* Login route with proper authentication check */}
              <Route path="/login" element={<LoginRoute />} />
              
              <Route path="/mes-donnees" element={
                <ProtectedRoute>
                  <MesDonnees />
                </ProtectedRoute>
              } />
              <Route path="/databases/:databaseId/manage" element={
                <ProtectedRoute>
                  <DatabaseManage />
                </ProtectedRoute>
              } />
              {/* Routes protégées */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Previsions />
                </ProtectedRoute>
              } />
              <Route path="/previsions" element={
                <ProtectedRoute>
                  <Previsions />
                </ProtectedRoute>
              } />
              <Route path="/approvisionnement" element={
                <ProtectedRoute>
                  <Approvisionnement />
                </ProtectedRoute>
              } />
              <Route path="/cycles-de-vie" element={
                <ProtectedRoute>
                  <CyclesDeVie />
                </ProtectedRoute>
              } />
              <Route path="/alertes" element={
                <ProtectedRoute>
                  <Alertes />
                </ProtectedRoute>
              } />
              <Route path="/promotions" element={
                <ProtectedRoute>
                  <Promotions />
                </ProtectedRoute>
              } />
              {/* RupturesDeStock route removed; replaced by Commandes tab 'Ruptures déclarées' */}
              <Route path="/commandes" element={
                <ProtectedRoute>
                  <Commandes />
                </ProtectedRoute>
              } />
            </Routes>
          </BrowserRouter>
        </DatabaseProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
